import React, { useState, useEffect } from 'react';
import { db } from '../../firebase'; // Ajusta la ruta si es necesario
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage"; // Importar Storage
import CategoriaForm from './CategoriaForm'; // Importar formulario
import SubcategoriaForm from './SubcategoriaForm'; // Importar form subcategoría
import Modal from './Modal'; // Importar Modal
// import CategoriaList from './CategoriaList';
import '../../styles/admin/GestionRecomendaciones.css'; 
import '../../styles/admin/Forms.css'; // Asegurar estilos de formulario
import '../../styles/admin/Modal.css'; // Asegurar estilos de Modal

const storage = getStorage(); // Inicializar Storage

function GestionRecomendaciones() {
  const [categorias, setCategorias] = useState([]);
  const [subcategorias, setSubcategorias] = useState({}); // Objeto para guardar subcategorías por ID de categoría
  const [loadingCategorias, setLoadingCategorias] = useState(true);
  const [loadingSubcategorias, setLoadingSubcategorias] = useState({}); // Estado de carga por categoría
  const [error, setError] = useState(null);
  const [showCategoriaForm, setShowCategoriaForm] = useState(false); // Estado para mostrar/ocultar form categoría
  const [editingCategoria, setEditingCategoria] = useState(null); // Estado para guardar datos al editar categoría
  const [uploadProgress, setUploadProgress] = useState(0); // Ahora podría ser un promedio o el último
  const [isUploading, setIsUploading] = useState(false); // Estado para indicar subida múltiple
  const [successMessage, setSuccessMessage] = useState('');

  // Nuevos estados para subcategorías
  const [showSubcategoriaForm, setShowSubcategoriaForm] = useState(false);
  const [editingSubcategoria, setEditingSubcategoria] = useState(null); // { id, nombre, ... }
  const [currentCategoriaIdForSub, setCurrentCategoriaIdForSub] = useState(null); // ID de la categoría padre del form actual

  // Estado para ver detalles de categoría
  const [expandedCategoriaId, setExpandedCategoriaId] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' o 'list'

  // --- Limpiar Mensaje de Éxito tras un tiempo ---
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 3000); // Ocultar después de 3 segundos
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // --- Lógica para cargar Categorías ---
  const fetchCategorias = async () => {
    setLoadingCategorias(true);
    setError(null);
    try {
      const categoriasCollection = collection(db, 'recomendaciones');
      const q = query(categoriasCollection, orderBy('orden', 'asc'), orderBy('nombre', 'asc'));
      const querySnapshot = await getDocs(q);
      const categoriasData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCategorias(categoriasData);
    } catch (err) {
      console.error("Error fetching categorias:", err);
      setError('Error al cargar categorías.');
    } finally {
      setLoadingCategorias(false);
    }
  };

  // --- Lógica para cargar Subcategorías de UNA Categoría ---
  const fetchSubcategorias = async (categoriaId) => {
    setLoadingSubcategorias(prev => ({ ...prev, [categoriaId]: true }));
    try {
      const subcategoriasCollection = collection(db, 'recomendaciones', categoriaId, 'subcategorias');
      const q = query(subcategoriasCollection, orderBy('orden', 'asc'), orderBy('nombre', 'asc'));
      const querySnapshot = await getDocs(q);
      const subcategoriasData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSubcategorias(prev => ({ ...prev, [categoriaId]: subcategoriasData }));
    } catch (err) {
      console.error(`Error fetching subcategorias for ${categoriaId}:`, err);
      if (err.code === 'failed-precondition') {
          setError(`Se necesita un índice para ordenar/filtrar subcategorías. Revisa la consola para el enlace de creación.`);
      }
    } finally {
      setLoadingSubcategorias(prev => ({ ...prev, [categoriaId]: false }));
    }
  };

  useEffect(() => {
    fetchCategorias();
    // Cargar todas las subcategorías de una vez
    const loadAllSubcategorias = async () => {
      if (categorias.length > 0) {
        await Promise.all(categorias.map(cat => fetchSubcategorias(cat.id)));
      }
    };
    if (categorias.length > 0) {
      loadAllSubcategorias();
    }
  }, [categorias.length === 0]);

  // --- Nuevos Helpers para Múltiples Imágenes --- 
  const uploadMultipleImages = async (files, pathPrefix, itemId) => {
      if (!files || files.length === 0) return [];
      setIsUploading(true);
      setUploadProgress(0); // Resetear progreso general
      
      const uploadPromises = files.map((file, index) => {
          const fileName = itemId ? `${itemId}_${Date.now()}_${index}_${file.name}` : `new_${Date.now()}_${index}_${file.name}`;
          const storagePath = `recomendaciones_imagenes/${pathPrefix}/${fileName}`;
          const storageRef = ref(storage, storagePath);
          const uploadTask = uploadBytesResumable(storageRef, file);
          
          return new Promise((resolve, reject) => {
              uploadTask.on('state_changed',
                  (snapshot) => {
                      // Podríamos calcular un progreso promedio, pero se complica.
                      // Mostramos el progreso del último archivo o un indicador general.
                      const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                      // Actualizar progreso de forma sencilla (ej. del último)
                      if (index === files.length - 1) setUploadProgress(progress);
                  },
                  (error) => reject(error),
                  async () => {
                      try {
                          // Obtener URL de la imagen
                          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                          
                          // Agregar a la galería principal (importamos esta función del módulo de galería)
                          try {
                              // Importar dinámicamente la función subirImagenGaleria 
                              const { subirImagenGaleria } = await import('../../data/galeria');
                              
                              // Crear metadatos para la galería
                              const metadata = {
                                  etiquetas: [pathPrefix], // Usar pathPrefix como etiqueta inicial
                                  descripcion: `Imagen subida desde ${pathPrefix}`
                              };
                              
                              // Agregar a la galería sin carpeta específica (root)
                              await subirImagenGaleria(
                                  file,      // El archivo original
                                  metadata,  // Metadatos para galería
                                  null       // Sin carpeta específica (root)
                              );
                              console.log(`Imagen ${file.name} agregada a la galería principal`);
                          } catch (galeriaError) {
                              // Si falla al agregar a la galería, solo registramos el error pero no abortamos
                              console.error("Error al agregar imagen a la galería:", galeriaError);
                          }
                          
                          // Devolver la URL para el uso original de la función
                          resolve(downloadURL);
                      } catch (error) {
                          reject(error);
                      }
                  }
              );
          });
      });

      try {
          const downloadURLs = await Promise.all(uploadPromises);
          setUploadProgress(0); 
          setIsUploading(false);
          return downloadURLs;
      } catch (error) {
          console.error("Error uploading multiple images:", error);
          setError('Error al subir una o más imágenes: ' + error.message);
          setIsUploading(false);
          setUploadProgress(0);
          // Importante: Si falla la subida, podríamos necesitar borrar las que sí se subieron.
          // Por simplicidad, ahora mismo no lo hacemos, pero es una mejora a considerar.
          throw error; // Relanzar para detener la operación
      }
  };

  const deleteMultipleImages = async (imageUrls) => {
      if (!imageUrls || imageUrls.length === 0) return;
      
      console.log('Intentando borrar imágenes:', imageUrls);
      
      const deletePromises = imageUrls.map(async url => {
          if (!url || !url.startsWith('https://firebasestorage.googleapis.com')) {
              console.log('URL inválida, saltando:', url);
              return;
          }
          
          try {
              const imageRef = ref(storage, url);
              await deleteObject(imageRef);
              console.log('Imagen borrada con éxito:', url);
          } catch (error) {
              // Verificar si es porque el objeto ya no existe
              if (error.code === 'storage/object-not-found') {
                  console.log('La imagen ya no existe, ignorando:', url);
              } else {
                  console.error('Error al borrar imagen:', error, url);
                  setError(prev => prev ? `${prev} | Error borrando imagen` : 'Error borrando imagen');
              }
          }
      });
      
      await Promise.allSettled(deletePromises);
  };

  // --- Funciones CRUD Categorías ---
  const handleAddCategoria = async (categoriaData, imagenFiles, imagesToDelete = [], galeriaUrls = [], existingUrls = []) => { // Recibe archivos
    setError(null);
    let uploadedUrls = [];
    try {
      uploadedUrls = await uploadMultipleImages(imagenFiles, 'categorias', null);
      
      // Combinar URLs subidas con las URLs de la galería
      // Mantener el orden exacto en que fueron proporcionadas
      categoriaData.imagenes = [...existingUrls, ...uploadedUrls, ...galeriaUrls]; // Guardar array de URLs
      
      await addDoc(collection(db, 'recomendaciones'), categoriaData);
      closeCategoriaModal();
      fetchCategorias();
      setSuccessMessage('¡Categoría añadida con éxito!');
    } catch (err) {
      setError(err.message || 'Error al añadir la categoría.');
      // Intentar borrar las imágenes subidas si falló Firestore
      if (uploadedUrls.length > 0) await deleteMultipleImages(uploadedUrls);
      throw err; 
    }
  };

  const handleUpdateCategoria = async (categoriaData, newImageFiles, imagesToDelete, galeriaUrls = [], existingUrls = []) => { // Recibe nuevos y a borrar
    if (!editingCategoria) return;
    setError(null);
    const categoriaId = editingCategoria.id;
    let newlyUploadedUrls = [];

    try {
      // 1. Borrar imágenes marcadas
      await deleteMultipleImages(imagesToDelete);
      
      // 2. Subir nuevas imágenes
      newlyUploadedUrls = await uploadMultipleImages(newImageFiles, 'categorias', categoriaId);

      // 3. Calcular array final de imágenes
      // Importante: Usamos existingUrls que ya viene en el orden correcto (después del drag & drop)
      categoriaData.imagenes = [...existingUrls, ...newlyUploadedUrls, ...galeriaUrls];

      // 4. Actualizar Firestore
      await updateDoc(doc(db, 'recomendaciones', categoriaId), categoriaData);

      closeCategoriaModal();
      fetchCategorias(); 
      setSuccessMessage('¡Categoría actualizada con éxito!');
    } catch (err) {
      setError(err.message || 'Error al actualizar la categoría.');
      // Si falló Firestore después de subir, intentar borrar las nuevas subidas
      if (newlyUploadedUrls.length > 0) await deleteMultipleImages(newlyUploadedUrls);
      // Nota: No re-añadimos las borradas en este punto por simplicidad.
      throw err; 
    }
  };

  const handleDeleteCategoria = async (categoriaId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta categoría? Esto borrará TODAS sus subcategorías e imágenes asociadas.')) {
      return;
    }
    setError(null);
    setLoadingCategorias(true); // Indicar carga durante el proceso

    try {
        // 1. Obtener info de la categoría (para sus imágenes)
        const categoriaDocData = categorias.find(c => c.id === categoriaId);
        const categoriaImagesToDelete = categoriaDocData?.imagenes || [];

        // 2. Obtener y borrar subcategorías y sus imágenes
        const subcategoriasCollectionRef = collection(db, 'recomendaciones', categoriaId, 'subcategorias');
        const subcategoriasSnapshot = await getDocs(subcategoriasCollectionRef);

        const deleteSubPromises = [];
        subcategoriasSnapshot.forEach((subDoc) => {
            const subData = subDoc.data();
            const subImagesToDelete = subData.imagenes || [];

            // Añadir promesa para borrar imágenes de la subcategoría
            if (subImagesToDelete.length > 0) {
                deleteSubPromises.push(deleteMultipleImages(subImagesToDelete));
            }
            // Añadir promesa para borrar el documento de la subcategoría
            deleteSubPromises.push(deleteDoc(subDoc.ref));
        });

        // Ejecutar todas las promesas de borrado de subcategorías y sus imágenes
        const results = await Promise.allSettled(deleteSubPromises);
        results.forEach(result => {
            if (result.status === 'rejected') {
                console.error("Error en borrado cascada (subcategoría o imagen):", result.reason);
                // Podríamos acumular errores aquí si quisiéramos ser más específicos
            }
        });

        // 3. Borrar el documento de la categoría en Firestore
        await deleteDoc(doc(db, 'recomendaciones', categoriaId));

        // 4. Borrar las imágenes de la categoría en Storage
        await deleteMultipleImages(categoriaImagesToDelete);

        // 5. Actualizar estado local
        fetchCategorias(); // Recarga la lista de categorías
        setSubcategorias(prev => {
             const newState = {...prev};
             delete newState[categoriaId]; // Elimina la entrada de subcategorías
             return newState;
        });
        if (expandedCategoriaId === categoriaId) {
            setExpandedCategoriaId(null); // Cierra el acordeón si estaba abierto
        }
        setSuccessMessage('¡Categoría y sus subcategorías eliminadas con éxito!');

    } catch (err) {
        console.error("Error al borrar la categoría y/o sus subcategorías:", err);
        setError(err.message || 'Error al borrar la categoría completa.');
        // El estado puede ser inconsistente si falla a mitad de camino
        fetchCategorias(); // Intentar recargar para reflejar el estado actual
    } finally {
        setLoadingCategorias(false);
    }
  };

  // --- Funciones CRUD Subcategorías ---
  const handleAddSubcategoria = async (subcategoriaData, newImageFiles, imagesToDelete = [], galeriaUrls = [], existingUrls = []) => {
      if (!currentCategoriaIdForSub) return;
      setError(null);
      let uploadedUrls = [];
      try {
        // Usa el helper para subir múltiples imágenes
        uploadedUrls = await uploadMultipleImages(newImageFiles, `subcategorias/${currentCategoriaIdForSub}`, null);
        // Combinar URLs subidas con las URLs de la galería
        // Mantener el orden exacto en que fueron proporcionadas
        subcategoriaData.imagenes = [...existingUrls, ...uploadedUrls, ...galeriaUrls]; // Guardar array de URLs

        const subcategoriasCollectionRef = collection(db, 'recomendaciones', currentCategoriaIdForSub, 'subcategorias');
        await addDoc(subcategoriasCollectionRef, subcategoriaData);

        closeSubcategoriaModal();
        fetchSubcategorias(currentCategoriaIdForSub); // Recargar subcategorías de esta categoría
        setSuccessMessage('¡Subcategoría añadida con éxito!');
      } catch (err) {
        setError(err.message || 'Error al añadir la subcategoría.');
        // Intentar borrar las imágenes subidas si falló Firestore
        if (uploadedUrls.length > 0) await deleteMultipleImages(uploadedUrls);
        throw err; // Relanzar para indicar fallo al formulario
      }
  };

  const handleUpdateSubcategoria = async (subcategoriaData, newImageFiles, imagesToDelete, galeriaUrls = [], existingUrls = []) => {
    if (!editingSubcategoria || !currentCategoriaIdForSub) return;
    setError(null);
    const categoriaId = currentCategoriaIdForSub;
    const subcategoriaId = editingSubcategoria.id;
    let newlyUploadedUrls = [];

    try {
        // 1. Borrar imágenes marcadas de Storage
        await deleteMultipleImages(imagesToDelete);

        // 2. Subir nuevas imágenes a Storage
        newlyUploadedUrls = await uploadMultipleImages(newImageFiles, `subcategorias/${categoriaId}`, subcategoriaId);

        // 3. Calcular el array final de imágenes
        // Importante: Usamos existingUrls que ya viene en el orden correcto (después del drag & drop)
        subcategoriaData.imagenes = [...existingUrls, ...newlyUploadedUrls, ...galeriaUrls];

        // 4. Actualizar el documento de la subcategoría en Firestore
        const subcategoriaDocRef = doc(db, 'recomendaciones', categoriaId, 'subcategorias', subcategoriaId);
        await updateDoc(subcategoriaDocRef, subcategoriaData);

        closeSubcategoriaModal();
        fetchSubcategorias(categoriaId); // Recargar subcategorías
        setSuccessMessage('¡Subcategoría actualizada con éxito!');
    } catch (err) {
        setError(err.message || 'Error al actualizar la subcategoría.');
        // Si falló Firestore después de subir, intentar borrar las nuevas imágenes subidas
        if (newlyUploadedUrls.length > 0) await deleteMultipleImages(newlyUploadedUrls);
        // Nota: No re-añadimos las borradas en este punto por simplicidad.
        throw err; // Relanzar para indicar fallo al formulario
    }
  };

  const handleDeleteSubcategoria = async (categoriaId, subId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta subcategoría y todas sus imágenes?')) {
        return;
    }
    setError(null);

    // Obtener las imágenes de la subcategoría ANTES de borrar el documento
    const subcategoriaDoc = subcategorias[categoriaId]?.find(sub => sub.id === subId);
    const imagesToDelete = subcategoriaDoc?.imagenes || []; // Array de URLs a borrar

    try {
        // 1. Borrar el documento de la subcategoría en Firestore
        const subcategoriaDocRef = doc(db, 'recomendaciones', categoriaId, 'subcategorias', subId);
        await deleteDoc(subcategoriaDocRef);

        // 2. Borrar las imágenes asociadas de Storage
        await deleteMultipleImages(imagesToDelete);

        fetchSubcategorias(categoriaId); // Recargar subcategorías
        setSuccessMessage('¡Subcategoría eliminada con éxito!');
    } catch (err) {
        setError(err.message || 'Error al borrar la subcategoría.');
        // Si falla Firestore, las imágenes ya se podrían haber borrado (o no)
        // Es difícil recuperar el estado exacto aquí sin transacciones complejas
    }
  };

  // --- Handlers para mostrar/ocultar formularios ---
  const openAddCategoriaModal = () => {
    closeSubcategoriaModal(); // Cerrar el otro form si está abierto
    setEditingCategoria(null);
    setShowCategoriaForm(true);
  };

  const openEditCategoriaModal = (categoria) => {
    closeSubcategoriaModal();
    setEditingCategoria(categoria);
    setShowCategoriaForm(true);
  };

  const closeCategoriaModal = () => {
    setShowCategoriaForm(false);
    setEditingCategoria(null);
    setError(''); // Limpiar errores al cerrar
  };

  const openAddSubcategoriaModal = (categoriaId) => {
      closeCategoriaModal(); // Cerrar form de categoría si abierto
      setCurrentCategoriaIdForSub(categoriaId);
      setEditingSubcategoria(null);
      setShowSubcategoriaForm(true);
  };

  const openEditSubcategoriaModal = (categoriaId, subcategoria) => {
      closeCategoriaModal();
      setCurrentCategoriaIdForSub(categoriaId);
      setEditingSubcategoria(subcategoria);
      setShowSubcategoriaForm(true);
  };

  const closeSubcategoriaModal = () => {
      setShowSubcategoriaForm(false);
      setEditingSubcategoria(null);
      setCurrentCategoriaIdForSub(null);
      setError('');
  };

  // --- Handler para ver detalles ---
  const toggleCategoriaExpansion = (categoriaId) => {
      const isOpening = expandedCategoriaId !== categoriaId;
      setExpandedCategoriaId(isOpening ? categoriaId : null);
      // Cargar subcategorías solo al expandir si no están cargadas
      if (isOpening && !subcategorias[categoriaId]) {
          fetchSubcategorias(categoriaId);
      }
  };

  // Toggle between grid and list view
  const toggleViewMode = () => {
    setViewMode(prev => prev === 'grid' ? 'list' : 'grid');
  };

  return (
    <div className="gestion-recomendaciones">
      <h2>Gestión de Recomendaciones</h2>

      {/* Mensajes Globales */} 
      {error && <p className="error-message global-error">{error}</p>}
      {successMessage && <p className="success-message global-success">{successMessage}</p>}
      {isUploading && <p className="info-message global-info">Subiendo imágenes... {uploadProgress.toFixed(0)}%</p>} 

      <section className="gestion-categorias">
        <div className="global-actions">
            <button onClick={openAddCategoriaModal} disabled={isUploading} className="add-button">
              <i className="fas fa-plus-circle"></i> Añadir Nueva Categoría
            </button>
            <button onClick={toggleViewMode} className="view-toggle-button">
              <i className={`fas fa-${viewMode === 'grid' ? 'list' : 'th'}`}></i> Ver como {viewMode === 'grid' ? 'Lista' : 'Grid'}
            </button>
        </div>
        
        {loadingCategorias ? (
          <div className="loading-container">
            <p>Cargando categorías...</p>
            <div className="loading-spinner"></div>
          </div>
        ) : (
          <>
            {categorias.length === 0 ? (
              <div className="empty-state">
                <p>No hay categorías creadas.</p>
                <button onClick={openAddCategoriaModal} className="add-button">
                  <i className="fas fa-plus-circle"></i> Crear primera categoría
                </button>
              </div>
            ) : (
              <div className={`categorias-container ${viewMode}-view`}>
                {categorias.map(cat => {
                  const isExpanded = expandedCategoriaId === cat.id;
                  const thumbnailUrl = cat.imagenes && cat.imagenes.length > 0 ? cat.imagenes[0] : null;
                  const hasSubs = subcategorias[cat.id] && subcategorias[cat.id].length > 0;
                  
                  return (
                    <div 
                      key={cat.id} 
                      className={`categoria-card ${isExpanded ? 'expanded' : ''}`}
                    >
                      <div className="categoria-card-header">
                        {thumbnailUrl ? (
                          <div className="categoria-card-image">
                            <img src={thumbnailUrl} alt={cat.nombre} />
                          </div>
                        ) : (
                          <div className="categoria-card-image placeholder">
                            <i className="fas fa-image"></i>
                          </div>
                        )}
                        
                        <div className="categoria-card-title">
                          <h4>{cat.nombre}</h4>
                          <span className="categoria-orden">Orden: {cat.orden ?? 'N/A'}</span>
                        </div>
                        
                        <div className="categoria-card-actions">
                          <button 
                            onClick={() => openEditCategoriaModal(cat)} 
                            disabled={isUploading}
                            className="edit-button"
                            title="Editar categoría"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button 
                            onClick={() => handleDeleteCategoria(cat.id)} 
                            className="delete-button" 
                            disabled={isUploading}
                            title="Eliminar categoría"
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                          <button 
                            onClick={() => toggleCategoriaExpansion(cat.id)}
                            className={`expand-button ${isExpanded ? 'expanded' : ''}`}
                            title={isExpanded ? 'Ocultar subcategorías' : 'Ver subcategorías'}
                          >
                            <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                          </button>
                        </div>
                      </div>
                      
                      {cat.descripcion && (
                        <div className="categoria-card-description">
                          <p>{cat.descripcion}</p>
                        </div>
                      )}
                      
                      {isExpanded && (
                        <div className="categoria-card-content">
                          <div className="subcategorias-header">
                            <h5>Subcategorías</h5>
                            <button 
                              onClick={() => openAddSubcategoriaModal(cat.id)} 
                              disabled={isUploading}
                              className="add-button"
                            >
                              <i className="fas fa-plus-circle"></i> Añadir
                            </button>
                          </div>
                          
                          {loadingSubcategorias[cat.id] ? (
                            <div className="loading-container">
                              <p>Cargando subcategorías...</p>
                              <div className="loading-spinner"></div>
                            </div>
                          ) : (
                            <div className="subcategorias-container">
                              {!subcategorias[cat.id] || subcategorias[cat.id].length === 0 ? (
                                <div className="empty-subcategorias">
                                  <p>No hay subcategorías creadas.</p>
                                  <button onClick={() => openAddSubcategoriaModal(cat.id)} className="add-button">
                                    <i className="fas fa-plus-circle"></i> Crear primera subcategoría
                                  </button>
                                </div>
                              ) : (
                                <div className="subcategorias-grid">
                                  {subcategorias[cat.id].map(sub => {
                                    const subThumbnailUrl = sub.imagenes && sub.imagenes.length > 0 ? sub.imagenes[0] : null;
                                    
                                    return (
                                      <div key={sub.id} className="subcategoria-card">
                                        {subThumbnailUrl ? (
                                          <div className="subcategoria-card-image">
                                            <img src={subThumbnailUrl} alt={sub.nombre} />
                                          </div>
                                        ) : (
                                          <div className="subcategoria-card-image placeholder">
                                            <i className="fas fa-image"></i>
                                          </div>
                                        )}
                                        
                                        <div className="subcategoria-card-content">
                                          <h5>{sub.nombre}</h5>
                                          <div className="subcategoria-meta">
                                            <span className="subcategoria-orden">Orden: {sub.orden ?? 'N/A'}</span>
                                            <div className="subcategoria-social">
                                              {sub.whatsapp && <i className="fab fa-whatsapp" title="Tiene WhatsApp"></i>}
                                              {sub.instagram && <i className="fab fa-instagram" title="Tiene Instagram"></i>}
                                              {sub.web && <i className="fas fa-globe" title="Tiene Sitio Web"></i>}
                                              {sub.ubicacion && <i className="fas fa-map-marker-alt" title="Tiene Ubicación"></i>}
                                            </div>
                                          </div>
                                          
                                          <div className="subcategoria-card-actions">
                                            <button 
                                              onClick={() => openEditSubcategoriaModal(cat.id, sub)} 
                                              disabled={isUploading}
                                              className="edit-button"
                                              title="Editar subcategoría"
                                            >
                                              <i className="fas fa-edit"></i>
                                            </button>
                                            <button 
                                              onClick={() => handleDeleteSubcategoria(cat.id, sub.id)} 
                                              className="delete-button" 
                                              disabled={isUploading}
                                              title="Eliminar subcategoría"
                                            >
                                              <i className="fas fa-trash-alt"></i>
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </section>

      {/* Modal para Formulario de Categoría */} 
      <Modal 
        isOpen={showCategoriaForm} 
        onClose={closeCategoriaModal} 
        title={editingCategoria ? 'Editar Categoría' : 'Añadir Nueva Categoría'}
        closeOnOutsideClick={false}
      >
        <CategoriaForm 
            onSubmit={editingCategoria ? handleUpdateCategoria : handleAddCategoria} 
            initialData={editingCategoria} 
            onCancel={closeCategoriaModal} // El botón cancelar del form también cierra el modal
        />
      </Modal>

      {/* Modal para Formulario de Subcategoría */} 
       <Modal 
        isOpen={showSubcategoriaForm} 
        onClose={closeSubcategoriaModal} 
        title={editingSubcategoria ? 'Editar Subcategoría' : 'Añadir Nueva Subcategoría'}
        closeOnOutsideClick={false}
      >
        <SubcategoriaForm 
            onSubmit={editingSubcategoria ? handleUpdateSubcategoria : handleAddSubcategoria}
            initialData={editingSubcategoria}
            onCancel={closeSubcategoriaModal}
        />
      </Modal>
    </div>
  );
}

export default GestionRecomendaciones; 